import { appointmentSlot, dayOf } from '@ghp/domain'
import {
  Button,
  EmptyState,
  Eyebrow,
  IconButton,
  InfoNote,
  Page,
  PageBody,
  PageHeader,
  SegmentedControl,
} from '@ghp/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createRoute,
  stripSearchParams,
  useNavigate,
  type SearchSchemaInput,
} from '@tanstack/react-router'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'

import { useNow } from '../../../app/clock'
import { useIsOnline } from '../../../app/network'
import { shellRoute } from '../../../app/routes/root'
import { useTeamData } from '../../../app/use-team-data'
import {
  anyScheduled,
  internalMeetingsBetween,
  scheduledBetween,
  type CachedInquiry,
  type CachedInternalMeeting,
  type OutboxEntry,
} from '../../../offline/data-db'
import { latestSubmissions } from '../../../offline/outbox'
import { pullInquiries, pullInternalMeetings } from '../../../offline/pull'
import {
  DRAFTS_QUERY_KEY,
  INQUIRIES_QUERY_KEY,
  INTERNAL_MEETINGS_QUERY_KEY,
  OUTBOX_QUERY_KEY,
} from '../../../offline/query-keys'
import { OpeningSheet } from '../../inspections/components/OpeningSheet'
import { usePassageNotice } from '../../inspections/use-passage-notice'
import { useFieldAppointment } from '../../inspections/use-field-appointment'
import { useAppointmentMove } from '../../inspections/use-appointment-move'
import { useTourOccupancy } from '../../inspections/use-tour-occupancy'
import { pullPresentationOf, type PullPresentation } from '../../inspections/labels'
import {
  hasQueuedHandOver,
  isTeamWork,
  tourStateOf,
  type TourState,
  type TransmissionRow,
} from '../../inspections/lib/list-filters'
import { usePassageNoticedAt, usePendingReminders } from '../../inspections/lib/passage-notices'
import { useLockedDrafts } from '../../inspections/lib/locked-drafts'
import {
  AGENDA_DEFAULTS,
  validateAgendaSearch,
  type AgendaSearch,
  type AgendaView,
} from '../lib/agenda-search'
import {
  monthRangeOf,
  monthWeeksOf,
  shiftDays,
  shiftMonths,
  today,
  weekRangeOf,
  workingWeekOf,
} from '../lib/agenda-calendar'
import { monthLabel, weekEyebrow, weekLabel } from '../lib/agenda-labels'
import { agendaItemsOf, isDone, itemKey, meetingsOnDays } from '../lib/agenda-layout'
import { MonthGrid } from '../components/MonthGrid'
import { WeekGrid } from '../components/WeekGrid'
import s from './agenda.module.css'

/**
 * « Agenda » — les rendez-vous de l'équipe, **lus en cache local** (D21), en semaine ou en mois,
 * et ses **points internes** (#319) : le créneau où le bureau l'attend n'est pas un créneau libre.
 *
 * ⚠️ La replanification n'est pas ici : la maquette ouvre une fenêtre « modifier le RDV » sur
 * chaque carte, mais le terrain ne modifie jamais la base (D4) et aucune proposition de
 * replanification n'existe. Une carte ouvre donc la **pré-fiche**, qui mène à la saisie.
 */
export const agendaRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/agenda',
  // ⚠️ `SearchSchemaInput` sépare ce qu'on **accepte** de ce qu'on **rend** : sans lui, TanStack
  // déduit l'entrée du retour et exige `search` sur chaque `<Link to="/agenda">` de l'app.
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): AgendaSearch =>
    validateAgendaSearch(search),
  search: {
    // La semaine d'aujourd'hui ne s'écrit pas dans l'URL : `/agenda` reste `/agenda`.
    middlewares: [stripSearchParams<AgendaSearch>(AGENDA_DEFAULTS)],
  },
  component: Agenda,
})

/**
 * Où en est la descente de la tournée, du point de vue de l'agent (cf. « Mes contrôles »).
 *
 * ⚠️ `connecting` s'ajoute au classement partagé (`labels.ts`), qui range « la session n'est pas
 * encore montée » (`no_session`) **avec les coupures réseau**. Le raccourci se tient partout
 * ailleurs — l'agent attend, et ça repart — mais pas ici : l'agenda est le premier écran ouvert
 * après un démarrage, donc celui qui tombe systématiquement dans cet état-là, tablette
 * connectée. Accuser le réseau à ce moment envoie chercher une panne qui n'existe pas.
 */
type TourDownload = 'running' | 'connecting' | PullPresentation

/**
 * Ce que l'écran dit d'un agenda vide, selon ce qui s'est passé. Un message unique — « aucun
 * rendez-vous » — couvrirait à tort le cas où la tournée n'est pas descendue : l'agent en
 * conclurait qu'il n'a rien à faire.
 */
const EMPTY_AGENDA: Record<TourDownload, { title: string; body: string }> = {
  running: {
    title: 'Téléchargement de la tournée…',
    body: 'Les rendez-vous de votre équipe sont en cours de réception.',
  },
  connecting: {
    title: 'Connexion au serveur…',
    body: "La tablette rouvre sa session avant de descendre les rendez-vous. Le réseau est là : il n'y a rien à faire. Si ce message reste, c'est le serveur qui ne répond pas — prévenez le bureau.",
  },
  done: {
    title: 'Aucun rendez-vous sur cette période',
    body: "Les contrôles dont le rendez-vous n'est pas encore pris n'apparaissent pas ici : ils restent dans « Mes contrôles », prêts à être saisis.",
  },
  refused: {
    title: 'Agenda inaccessible',
    body: "Le serveur a refusé l'accès aux contrôles avec ce compte. Attendre le réseau n'y changera rien : reconnectez-vous avec un compte d'agent de terrain, et prévenez le bureau si le refus persiste.",
  },
  unreachable: {
    title: 'Aucun rendez-vous connu',
    body: "La tournée n'a pas encore été téléchargée. Elle descendra dès que la tablette retrouvera du réseau, et les rendez-vous avec elle.",
  },
  faulty: {
    title: 'Agenda indisponible',
    body: "La tournée n'a pas pu être téléchargée, et ce n'est pas une affaire de réseau : prévenez le bureau, elle ne descendra pas d'elle-même.",
  },
}

function Agenda() {
  const { shared, sync, accessToken, auth, requestSync, planning } = agendaRoute.useRouteContext()
  const data = useTeamData()
  const queryClient = useQueryClient()
  const recordNotice = usePassageNotice(auth, requestSync)
  const recordAppointmentOnSite = useFieldAppointment(auth, requestSync)
  const appointmentMove = useAppointmentMove({ auth, planning, accessToken, requestSync })
  const occupancy = useTourOccupancy()
  const { view, day } = agendaRoute.useSearch()
  const navigate = useNavigate({ from: agendaRoute.fullPath })
  const [download, setDownload] = useState<TourDownload>('running')
  const isOnline = useIsOnline()
  /**
   * L'état de la session, **observé** et non lu une fois : elle entre dégradée au démarrage
   * (D22 pt.1) et monte en ligne toute seule quelques instants plus tard. Sans cet abonnement,
   * l'effet ci-dessous ne rejouerait jamais — la base d'équipe, elle, ne change pas quand la
   * session monte en ligne — et l'écran resterait vide pour toute sa durée de vie, tournée
   * disponible.
   */
  const sessionState = useSyncExternalStore(auth.subscribe, () => auth.getSnapshot().state)
  /** Le contrôle dont la pré-fiche est ouverte — `null` tant qu'aucune carte n'a été touchée. */
  const [opening, setOpening] = useState<CachedInquiry | null>(null)

  // Une ancre absente vaut **aujourd'hui**, et n'entre pas dans l'URL : c'est ce que l'agent
  // veut voir en ouvrant l'écran, et ce sur quoi un rechargement doit le ramener.
  const anchor = day === '' ? today() : day
  const range = useMemo(
    () => (view === 'week' ? weekRangeOf(anchor) : monthRangeOf(anchor)),
    [view, anchor],
  )

  /**
   * La période affichée, lue **par l'index du jour de rendez-vous** (`scheduledBetween`) : le
   * cache des demandes garde tout contrôle saisi, filtrer `all()` ferait payer tout l'historique de la
   * tablette à chaque flèche de navigation.
   *
   * Les bornes entrent dans la clé Query : deux semaines sont deux lectures, et l'invalidation
   * posée sur le préfixe `['inquiries']` les atteint toutes les deux.
   */
  const scheduled = useQuery({
    queryKey: [...INQUIRIES_QUERY_KEY, 'agenda', range.from, range.to],
    queryFn: () => scheduledBetween(data, range.from, range.to),
  })

  /**
   * Les points internes de la période, par le même index de jour que les rendez-vous. Lus à part
   * — leur table est à part (`CachedInternalMeeting`) —, posés ensemble par la grille.
   */
  const meetings = useQuery({
    queryKey: [...INTERNAL_MEETINGS_QUERY_KEY, range.from, range.to],
    queryFn: () => internalMeetingsBetween(data, range.from, range.to),
  })

  /**
   * La file d'envoi, lue pour **l'état** des cartes et non pour la synchro : c'est le dépôt
   * accepté par le serveur qui dit qu'un contrôle a quitté l'équipe, le statut en cache ne le
   * disant jamais (#181). L'observateur `liveQuery` invalide cette clé, donc une carte change
   * de pastille à la seconde où l'envoi aboutit, sans que l'écran n'interroge rien.
   *
   * La file est repliée **une fois** en carte : rien n'en supprime jamais d'entrée, elle porte
   * tous les envois de la vie de la tablette et une interrogation par carte la rebalaierait
   * autant de fois qu'il y a de rendez-vous.
   */
  const outbox = useQuery({
    queryKey: OUTBOX_QUERY_KEY,
    queryFn: () => data.outbox.toArray(),
  })
  const submissions = useMemo(() => latestSubmissions(outbox.data ?? []), [outbox.data])
  const locked = useLockedDrafts()

  /**
   * Le contrôle est-il encore le travail de l'équipe (`isTeamWork`) — ce que l'état vide lit pour
   * dire si la tablette tient une tournée. La grille, elle, montre tout : un contrôle transmis, ou
   * dont la transmission attend en file, reste à sa place, grisé (#577).
   *
   * ⚠️ Rien ne se montre avant que la file **et** les verrous soient lus : la grille peindrait un
   * instant, en couleur et comme à faire, un contrôle déjà transmis.
   *
   * ⚠️ **Lus, pas réussis** : une lecture refusée (IndexedDB plein, base abîmée) laisserait la
   * grille vide sous « Aucun rendez-vous », et l'équipe croirait sa journée libre. Mieux vaut
   * montrer en couleur un contrôle transmis que cacher le travail du jour (M21).
   */
  const ready = !outbox.isPending && locked.readAt !== null
  const isWork = useMemo(
    () => (inquiry: CachedInquiry) =>
      isTeamWork(transmissionOf(inquiry, submissions), locked.ids.has(inquiry.id)),
    [submissions, locked.ids],
  )
  /** Une transmission attend en file : la pré-fiche n'offre que la consultation. */
  const isQueued = useMemo(
    () => (inquiry: CachedInquiry) =>
      hasQueuedHandOver(submissions.get(inquiry.id)?.state ?? null, locked.ids.has(inquiry.id)),
    [submissions, locked.ids],
  )

  /**
   * Le cache porte-t-il des rendez-vous **hors de la période affichée** ? C'est ce qui sépare les
   * deux faits que l'état vide confondait : une semaine creuse est un fait, l'absence de tournée
   * en est un autre. Hors période, parce que la question ne se pose que sur une période vide.
   *
   * ⚠️ La réponse dépend de la file et des verrous autant que du cache : leurs instants de
   * lecture entrent dans la clé, sans quoi un envoi accepté laisserait l'état vide compter un
   * contrôle qui n'est plus le travail de l'équipe. La réponse précédente tient lieu d'attente — sans elle,
   * chaque écriture de la file ferait reclignoter « Téléchargement de la tournée… ».
   */
  const knownElsewhere = useQuery({
    queryKey: [...INQUIRIES_QUERY_KEY, 'agenda', 'any', outbox.dataUpdatedAt, locked.readAt],
    queryFn: () => anyScheduled(data, isWork),
    enabled: ready,
    placeholderData: (previous) => previous,
  })

  /**
   * La tournée redescend à l'ouverture de l'écran (D22 pt.2), comme sur « Mes contrôles » : un
   * agent qui recharge l'app sur `/agenda` — ce qu'une tablette fait pour de vrai — ne doit pas
   * attendre le tour de planificateur suivant pour voir sa journée.
   *
   * ⚠️ **La base d'équipe est la seule dépendance qui varie** : sans elle, une tablette qui
   * change de main lirait la base de l'entrante sans jamais demander sa tournée.
   */
  useEffect(() => {
    // Sans jeton, il n'y a rien à descendre — et rien à dire d'ici : ce que l'écran affiche
    // pendant ce temps se dérive au rendu (`tour`), d'où l'absence d'`isOnline` ci-dessous.
    if (accessToken() === null) return

    let showing = true

    setDownload('running')

    // ⚠️ **À côté de la tournée, pas dans son état** : `download` dit si l'agent a ses
    // contrôles, et c'est ce que l'état vide explique. Un point interne qui ne descend pas ne
    // retire rien à la journée de travail — la grille garde ceux d'hier, la passe suivante
    // reprendra.
    void pullInternalMeetings(data, sync, accessToken(), (token) => auth.tokenRefused(token)).then(
      (result) => {
        if (result.outcome !== 'unavailable') {
          void queryClient.invalidateQueries({ queryKey: INTERNAL_MEETINGS_QUERY_KEY })
        }
      },
      (failure: unknown) => {
        console.error('[terrain] Descente des points internes impossible', failure)
      },
    )

    pullInquiries(data, shared, sync, accessToken(), (token) => auth.tokenRefused(token)).then(
      (result) => {
        if (result.outcome !== 'unavailable') {
          void queryClient.invalidateQueries({ queryKey: INQUIRIES_QUERY_KEY })
          void queryClient.invalidateQueries({ queryKey: DRAFTS_QUERY_KEY })
        }

        if (showing) setDownload(pullPresentationOf(result))
      },
      (failure: unknown) => {
        console.error('[terrain] Descente de la tournée impossible', failure)
        if (showing) setDownload('faulty')
      },
    )

    return () => {
      showing = false
    }
  }, [data, shared, sync, accessToken, auth, queryClient, sessionState])

  const days = useMemo(() => workingWeekOf(anchor), [anchor])
  const weeks = useMemo(() => monthWeeksOf(anchor), [anchor])
  // Mémoïsé sur la lecture : ce tableau nourrit la reprise hors grille, et un littéral neuf à
  // chaque rendu la recalculerait à chaque frappe d'état de l'écran.
  const appointments = useMemo(() => (ready ? (scheduled.data ?? []) : []), [ready, scheduled.data])
  // Un séminaire (#576) se pose sur chacun des jours qu'il occupe.
  const internalMeetings = useMemo(
    () => meetingsOnDays(meetings.data ?? [], range.from, range.to),
    [meetings.data, range.from, range.to],
  )

  /**
   * L'état de tournée de chaque rendez-vous affiché — **la règle de « Mes contrôles »**, et non
   * une seconde façon de lire la même chose : deux écrans qui montrent le même contrôle ne
   * peuvent pas s'en dire l'un transmis et l'autre encore planifié (#181).
   */
  const noticedAt = usePassageNoticedAt()
  const pendingReminders = usePendingReminders()
  const tourStates = useMemo(
    () =>
      new Map<string, TourState>(
        appointments.map((inquiry) => [
          inquiry.id,
          tourStateOf({
            ...transmissionOf(inquiry, submissions),
            passageNoticedAt: noticedAt.get(inquiry.id) ?? null,
          }),
        ]),
      ),
    [appointments, submissions, noticedAt],
  )

  /**
   * Ce qui est **fait** (#577) — un créneau fini, un contrôle transmis ou en file —, et le jour
   * avant lequel tout est passé : l'agenda les grise, et c'est l'avancée de la journée que l'équipe
   * y lit. Relu chaque minute (`useNow`) : un rendez-vous qui se termine écran ouvert s'estompe à
   * l'heure dite.
   */
  const now = useNow()
  const todayDay = dayOf(now)
  const done = useMemo(
    () =>
      new Set(
        agendaItemsOf(appointments, internalMeetings)
          .filter((item) =>
            item.kind === 'appointment'
              ? isDone(item, now, tourStates.get(item.inquiry.id), isQueued(item.inquiry))
              : isDone(item, now, undefined, false),
          )
          .map(itemKey),
      ),
    [appointments, internalMeetings, now, tourStates, isQueued],
  )

  /**
   * Les rendez-vous que la grille de la semaine n'a pas de colonne pour montrer — un dimanche,
   * en pratique. Rien n'interdit d'en poser un, et un rendez-vous qu'aucun écran n'affiche est
   * un rendez-vous manqué (M21) : la semaine les reprend en clair sous la grille.
   */
  const offGrid = useMemo(
    () =>
      view === 'week'
        ? appointments.filter((inquiry) => !days.includes(inquiry.scheduledDay ?? ''))
        : [],
    [view, appointments, days],
  )
  // Même reprise pour un point interne du dimanche : rare, mais une réunion qu'aucun écran
  // n'affiche est une réunion manquée.
  const offGridMeetings = useMemo(
    () =>
      view === 'week'
        ? internalMeetings.filter((meeting) => !days.includes(meeting.scheduledDay ?? ''))
        : [],
    [view, internalMeetings, days],
  )

  /**
   * Où en est la tournée, **jeton compris**. Sans jeton, rien n'a été demandé donc rien n'a
   * échoué : `pullInquiries` rendrait `no_session`, que le classement partagé range avec les
   * coupures, et l'écran dirait « dès que la tablette retrouvera du réseau » à chaque démarrage
   * à froid — tablette connectée. Ce que l'agent doit distinguer à cet instant, c'est
   * l'interface réseau : coupée, c'est bien le réseau qu'il attend.
   *
   * ⚠️ **Dérivé au rendu, et non posé depuis l'effet.** L'y poser obligeait `isOnline` à figurer
   * dans les dépendances de la descente : chaque bascule `online`/`offline` — le mode dégradé
   * normal du terrain — rejouait alors l'effet entier, remettait `download` à `running` (l'état
   * vide reclignote) et relançait `pullInquiries`, qui pagine jusqu'à `MAX_PAGES`. Ici le
   * message suit le réseau à la milliseconde **sans que rien ne soit redemandé**.
   */
  const tour: TourDownload =
    accessToken() === null ? (isOnline ? 'connecting' : 'unreachable') : download

  const emptiness = emptyAgendaOf(tour, knownElsewhere.data ?? false, knownElsewhere.isPending)

  const goTo = (nextDay: string, nextView: AgendaView = view): void => {
    // ⚠️ `replace` : la période reste dans l'URL (D22), mais dix pas de semaine ne sont pas dix
    // écrans. Sans lui, quitter l'agenda demande autant de retours arrière qu'on a navigué —
    // au doigt, sur une tablette. C'est déjà le geste des écrans à filtres du back-office.
    void navigate({
      search: { view: nextView, day: nextDay === today() ? '' : nextDay },
      replace: true,
    })
  }

  const step = (direction: -1 | 1): void => {
    goTo(view === 'week' ? shiftDays(anchor, direction * 7) : shiftMonths(anchor, direction))
  }

  return (
    <Page>
      <PageHeader eyebrow="Rendez-vous de l’équipe" title="Agenda" />
      <PageBody>
        <div className={s.bar}>
          <div className={s.steps}>
            {/* La période est un **titre de section**, pas celui de l'écran : le titre nomme
                l'écran, comme partout ailleurs dans l'app — c'est ce que lit le sélecteur de
                tâches d'une PWA installée, où l'URL n'existe pas (`app/document-title.ts`). */}
            <div className={s.heading}>
              {/* L'eyebrow du prototype (`agTitleHtml`) : le numéro de semaine, ou la nature de
                  la période en vue Mois, qui n'en a pas. */}
              <Eyebrow className={s.periodEyebrow}>
                {view === 'week' ? weekEyebrow(anchor) : 'Planning mensuel'}
              </Eyebrow>
              <h2 className={s.period}>
                {view === 'week'
                  ? weekLabel(range.from, days.at(-1) ?? range.to)
                  : monthLabel(anchor)}
              </h2>
            </div>
            {/* ⚠️ Un `IconButton` et non un `Button size="sm"` : ce dernier se dimensionne sur
                son contenu, soit ~40 px de large pour une icône seule. Le prototype donne
                46 px à son `.icon-btn`, et l'app tourne en densité tactile — une cible du
                prototype ne doit pas rétrécir chez nous. */}
            <IconButton
              label={view === 'week' ? 'Semaine précédente' : 'Mois précédent'}
              onClick={() => {
                step(-1)
              }}
            >
              <ChevronLeft size={20} aria-hidden />
            </IconButton>
            <Button
              tone="neutral"
              size="sm"
              onClick={() => {
                goTo(today())
              }}
            >
              Aujourd’hui
            </Button>
            <IconButton
              label={view === 'week' ? 'Semaine suivante' : 'Mois suivant'}
              onClick={() => {
                step(1)
              }}
            >
              <ChevronRight size={20} aria-hidden />
            </IconButton>
          </div>
          <SegmentedControl
            label="Vues de l’agenda"
            value={view}
            onValueChange={(next) => {
              goTo(anchor, next as AgendaView)
            }}
            options={[
              { value: 'week', label: 'Semaine' },
              { value: 'month', label: 'Mois' },
            ]}
          />
        </div>

        {/* Un point interne seul suffit à montrer la grille : la semaine n'est pas vide pour
            l'équipe, et l'état vide parlerait de la tournée — qui est peut-être bien là. */}
        {appointments.length === 0 && internalMeetings.length === 0 ? (
          <EmptyState
            icon={<CalendarDays size={18} aria-hidden />}
            title={EMPTY_AGENDA[emptiness].title}
          >
            {EMPTY_AGENDA[emptiness].body}
          </EmptyState>
        ) : view === 'week' ? (
          <>
            <WeekGrid
              days={days}
              inquiries={appointments}
              meetings={internalMeetings}
              states={tourStates}
              today={todayDay}
              done={done}
              onOpen={setOpening}
            />
            {offGrid.length + offGridMeetings.length > 0 && (
              // ⚠️ Les boutons sont **à côté** de la note, pas dedans : `InfoNote` range tous ses
              // enfants dans un seul élément de texte — c'est ce qui lui fait composer une phrase
              // correctement —, donc aucun `gap` posé sur la note ne les séparerait. Ils sortent
              // au passage d'un `<p>`, où ils n'avaient rien à faire.
              <div className={s.offGrid}>
                <InfoNote icon={<CalendarDays size={15} />}>
                  La grille s’arrête au samedi.{' '}
                  {offGridSentence(offGrid.length, offGridMeetings.length)}
                </InfoNote>
                <div className={s.offGridList}>
                  {offGrid.map((inquiry) => (
                    <Button
                      key={inquiry.id}
                      tone="ghost"
                      size="sm"
                      onClick={() => {
                        setOpening(inquiry)
                      }}
                    >
                      {offGridLabel(inquiry)}
                    </Button>
                  ))}
                  {/* Du texte, pas un bouton : un point interne n'ouvre rien. */}
                  {offGridMeetings.map((meeting) => (
                    <span key={meeting.id} className={s.offGridMeeting}>
                      {offGridMeetingLabel(meeting)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <MonthGrid
            weeks={weeks}
            anchor={anchor}
            inquiries={appointments}
            meetings={internalMeetings}
            today={todayDay}
            done={done}
            onOpen={setOpening}
            onOpenDay={(nextDay) => {
              goTo(nextDay, 'week')
            }}
          />
        )}

        <OpeningSheet
          inquiry={opening}
          // Sans lui, la pré-fiche ouverte depuis l'agenda proposait « Ouvrir le contrôle » sur
          // un contrôle déjà transmis : l'agenda tient l'état de tournée, il le passe.
          state={opening === null ? undefined : tourStates.get(opening.id)}
          // Transmis ou en file, le contrôle reste dans l'agenda (#577) : la pré-fiche l'ouvre
          // en consultation, comme depuis la vue « Transmis » de « Mes contrôles ».
          queued={opening !== null && isQueued(opening)}
          onClose={() => {
            setOpening(null)
          }}
          onPassageNotice={recordNotice}
          pendingReminders={opening === null ? 0 : (pendingReminders.get(opening.id) ?? 0)}
          onAppointment={recordAppointmentOnSite}
          move={appointmentMove}
          tour={occupancy}
          onOpen={(inquiry) => {
            setOpening(null)
            void navigate({ to: '/inspections/$inquiryId', params: { inquiryId: inquiry.id } })
          }}
        />
      </PageBody>
    </Page>
  )
}

/**
 * Ce qu'une période vide veut dire, **une fois séparés les deux faits que l'écran confondait** :
 * « cette période ne porte aucun rendez-vous » n'est pas « la tournée n'est pas descendue ».
 *
 * Le cache est la source de vérité des lectures (D21) : s'il porte des rendez-vous ailleurs, la
 * tournée **est** là, quoi qu'ait donné la descente de cet instant — un agent hors réseau devant
 * une semaine creuse lit alors le fait, pas une panne. Les deux états qui appellent un geste,
 * eux, gardent leur mot : un refus se règle par une reconnexion et une panne par un appel au
 * bureau, et le cache n'y change rien.
 *
 * ⚠️ `stillCounting` est le troisième cas, et il n'est pas décoratif : tant que le compte local
 * n'a pas répondu, on ne **sait** pas si la tournée est là. Trancher sur `false` en attendant
 * fait accuser une tablette dont le cache est peut-être plein — hors ligne, la descente rend
 * `unreachable` sans attendre le réseau, donc avant Dexie, et l'agent lit « La tournée n'a pas
 * encore été téléchargée » sur une tablette qui a tout. Mieux vaut dire qu'on cherche encore.
 */
function emptyAgendaOf(
  download: TourDownload,
  knownElsewhere: boolean,
  stillCounting: boolean,
): TourDownload {
  const waiting = download === 'running' || download === 'connecting' || download === 'unreachable'

  if (!waiting) return download
  if (stillCounting) return 'running'

  return knownElsewhere ? 'done' : download
}

/** Ce que la file dit de ce contrôle : l'état de son dernier envoi, et quand il a été accepté. */
function transmissionOf(
  inquiry: CachedInquiry,
  submissions: ReadonlyMap<string, OutboxEntry>,
): TransmissionRow {
  const submission = submissions.get(inquiry.id)

  return { inquiry, sync: submission?.state ?? null, sentAt: submission?.sentAt ?? null }
}

/**
 * La phrase qui annonce la reprise du dimanche, accordée à ce qu'elle reprend : des rendez-vous,
 * des points internes, ou les deux.
 */
function offGridSentence(appointments: number, meetings: number): string {
  if (meetings === 0) {
    return appointments > 1
      ? 'Ces rendez-vous tombent un dimanche :'
      : 'Ce rendez-vous tombe un dimanche :'
  }

  if (appointments === 0) {
    return meetings > 1
      ? 'Ces points internes tombent un dimanche :'
      : 'Ce point interne tombe un dimanche :'
  }

  return 'Ces créneaux tombent un dimanche :'
}

/** « Point interne 09:00 · Brief hebdo » — sa nature d'abord, puisque rien d'autre ne la dit. */
function offGridMeetingLabel(meeting: CachedInternalMeeting): string {
  const time = appointmentSlot(meeting.scheduledAt)?.time

  return time === undefined
    ? `Point interne · ${meeting.title}`
    : `Point interne ${time} · ${meeting.title}`
}

/** « 08:30 · GHP-2026-0142 » — de quoi reconnaître un rendez-vous hors grille sans l'ouvrir. */
function offGridLabel(inquiry: CachedInquiry): string {
  const time = appointmentSlot(inquiry.scheduledAt ?? '')?.time
  const reference = inquiry.reference ?? 'Sans référence'

  return time === undefined ? reference : `${time} · ${reference}`
}
